import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Card from '../components/Card';

const stats = [
  { value: '6000+', label: 'Lives Impacted' },
  { value: '4',    label: 'Core Programs' },
  { value: '50+',  label: 'Volunteers' },
  { value: '3+',   label: 'Years of Service' },
];

const initiatives = [
  {
    title: 'Pad Drives',
    description: 'Providing essential hygiene products for girls and women in need.',
    icon: (
      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    title: 'Mentorship for Change',
    description: 'Connecting youth with experienced mentors to build skills for a brighter future.',
    icon: (
      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    title: 'Inspiring a Loving World',
    description: 'Promoting compassion and community care through outreach programs.',
    icon: (
      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
      </svg>
    ),
  },
  {
    title: 'Digital Literacy',
    description: 'Equipping individuals with crucial digital skills for the modern world.',
    icon: (
      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
];

function Home() {
  return (
    <div>
      <Hero />

      {/* Stats strip */}
      <div className="bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {stats.map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl md:text-4xl font-black">{value}</p>
                <p className="text-gray-400 text-sm mt-1 font-medium">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block text-red-600 text-sm font-semibold uppercase tracking-widest mb-4">Our Mission</span>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-6">
            Building a world of<br />
            <span className="text-red-600">opportunity for all</span>
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed max-w-2xl mx-auto">
            At Khisa Foundation, we strive to empower lives and shape futures through impactful programs and community support. We provide essential resources, mentorship, and digital skills to underserved communities — fostering a world of compassion, care, and opportunity.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/about"
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3.5 rounded-full transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5">
              About Us
            </Link>
            <Link to="/get-involved"
              className="border-2 border-gray-200 hover:border-red-300 text-gray-700 hover:text-red-600 font-semibold px-8 py-3.5 rounded-full transition-all duration-200">
              Get Involved
            </Link>
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block text-red-600 text-sm font-semibold uppercase tracking-widest mb-3">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900">Our Initiatives</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {initiatives.map((item) => (
              <Card key={item.title} {...item} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/initiatives"
              className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition-colors">
              See all initiatives
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-24 bg-gray-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-black mb-5 leading-tight">
            Ready to make a <span className="text-red-400">difference?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Your support — whether time, money, or expertise — directly transforms lives in our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate"
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-4 rounded-full transition-all duration-200 shadow-lg shadow-red-600/20 hover:-translate-y-0.5">
              Donate Today
            </Link>
            <Link to="/get-involved"
              className="border-2 border-gray-600 hover:border-gray-400 text-gray-300 hover:text-white font-semibold px-10 py-4 rounded-full transition-all duration-200">
              Volunteer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
